import { useEffect } from "react";

/**
 * A window-level shortcut, bound for as long as the component is mounted.
 *
 * `metaKey || ctrlKey` rather than a platform sniff: Cmd on a Mac and Ctrl
 * everywhere else is the convention, and accepting both is correct on either
 * because no browser sends the other one for this chord.
 *
 * Bound on `window` rather than on the input, because the whole point of the
 * shortcut is to reach a control that does not have focus.
 */
export function useHotkey(key: string, onPress: () => void): void {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      if (event.key.toLowerCase() !== key) return;
      // Cmd+K is Chrome's "search with" and Firefox's search-bar focus, so
      // this has to claim it rather than fire alongside it.
      event.preventDefault();
      onPress();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [key, onPress]);
}
